import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperTextProps,
	IconButton,
	InputAdornment,
	LinearProgress,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import zxcvbn from 'zxcvbn';
import {
	CHANGE_PASSWORD_ACTION_TEXT,
	CHANGE_PASSWORD_CANCEL_ACTION_TEXT,
	CHANGE_PASSWORD_CONFIRM_ACTION_TEXT,
	CHANGE_PASSWORD_CURRENT_LABEL,
	CHANGE_PASSWORD_NEW_LABEL,
	CHANGE_PASSWORD_REPEAT_WARNING,
	CHANGE_PASSWORD_STRENGTH_LABEL_FAIR,
	CHANGE_PASSWORD_STRENGTH_LABEL_GOOD,
	CHANGE_PASSWORD_STRENGTH_LABEL_STRONG,
	CHANGE_PASSWORD_STRENGTH_LABEL_WEAK,
	CHANGE_PASSWORD_SUBHEADER,
} from '../data/labelsAndMessages';
import FieldLabel from './fields/FieldLabel';
interface Props {
	open: boolean;
	onCancel: () => void;
	onConfirm: (oldPassword: string, password: string) => Promise<boolean>;
}

const ChangePasswordModal = ({ open, onCancel, onConfirm }: Props) => {
	const [oldPassword, setOldPassword] = useState('');
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [validationResult, setValidationResult] = useState(
		{} as zxcvbn.ZXCVBNResult,
	);

	const uniqueNewPassword = password !== oldPassword;

	const isFormValid = !!oldPassword && !!password && uniqueNewPassword;

	useEffect(() => {
		getValidationScore(password);
	}, [password]);

	const getValidationScore = (password: string) => {
		const result = zxcvbn(password);
		setValidationResult(result);
	};

	const createPasswordLabel = () => {
		switch (validationResult.score) {
			case 0:
				return CHANGE_PASSWORD_STRENGTH_LABEL_WEAK();
			case 1:
				return CHANGE_PASSWORD_STRENGTH_LABEL_WEAK();
			case 2:
				return CHANGE_PASSWORD_STRENGTH_LABEL_FAIR();
			case 3:
				return CHANGE_PASSWORD_STRENGTH_LABEL_GOOD();
			case 4:
				return CHANGE_PASSWORD_STRENGTH_LABEL_STRONG();
			default:
				return CHANGE_PASSWORD_STRENGTH_LABEL_WEAK();
		}
	};

	const passwordStrengthColor = () => {
		switch (validationResult.score) {
			case 0:
				return 'error';
			case 1:
				return 'error';
			case 2:
				return 'warning';
			case 3:
				return 'success';
			case 4:
				return 'secondary';
			default:
				return 'error';
		}
	};

	const PasswordStrengthTooltip = ({ children }: FormHelperTextProps) => {
		let tooltipContent = [...validationResult.feedback.suggestions];
		if (!uniqueNewPassword) {
			tooltipContent = [CHANGE_PASSWORD_REPEAT_WARNING()];
		}
		const error = !!password && !!oldPassword && !uniqueNewPassword;
		return (
			<Stack
				direction='row'
				alignItems='flex-end'
				spacing={1}
				sx={{ position: 'relative', top: '0.5rem' }}
				color='textSecondary'
			>
				<Typography
					variant='caption'
					color={error ? 'error' : 'textSecondary'}
					data-testid='password-change--strength-score'
				>
					{children}
				</Typography>
				<Tooltip
					title={tooltipContent.join('\n')}
					data-testid='password-change--strength-tooltip'
				>
					<InfoOutlinedIcon
						color={error ? 'error' : 'inherit'}
						fontSize='small'
					/>
				</Tooltip>
			</Stack>
		);
	};

	const renderTextField = (
		title: string,
		value: string,
		name: string,
		onChange: (value: React.SetStateAction<string>) => void,
		showPassword: boolean,
		setShowPassword: (value: React.SetStateAction<boolean>) => void,
		error?: boolean,
		helperText?: string,
	) => {
		return (
			<FormControl>
				<FieldLabel title={title} />
				<TextField
					fullWidth
					name={name}
					value={value}
					onChange={(e) => {
						onChange(e.target.value);
					}}
					type={showPassword ? 'text' : 'password'}
					InputProps={{
						endAdornment: (
							<InputAdornment position='end'>
								<IconButton
									aria-label='toggle password visibility'
									onClick={() => setShowPassword(!showPassword)}
									edge='end'
								>
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						),
					}}
					inputProps={{
						'data-testid': 'password-change--' + name,
						// disable autocomplete for password fields when opening modal
						autocomplete: 'new-password',
					}}
					error={error}
					helperText={helperText}
					FormHelperTextProps={
						name === 'password'
							? {
									//@ts-expect-error - known typescript type bug in MUI when passing 'component' props
									component: PasswordStrengthTooltip,
							  }
							: {}
					}
				/>
			</FormControl>
		);
	};

	const formContent = () => {
		const passwordStrengthProgressColor = passwordStrengthColor();
		return (
			<Stack spacing={4}>
				{renderTextField(
					CHANGE_PASSWORD_CURRENT_LABEL(),
					oldPassword,
					'oldPassword',
					setOldPassword,
					showOldPassword,
					setShowOldPassword,
				)}
				{renderTextField(
					CHANGE_PASSWORD_NEW_LABEL(),
					password,
					'password',
					setPassword,
					showPassword,
					setShowPassword,
					false,
					!!password && !!validationResult && createPasswordLabel(),
				)}
				{!!password && !!validationResult && (
					<LinearProgress
						variant='determinate'
						// LinearProgress expects value of [0-100]; multiple zxcvbn's validation score [0-4] by 25 to get appropriate value
						value={(validationResult.score ?? 0) * 25}
						sx={{
							marginTop: '0 !important',
							transition: 'none !important',
							bottom: '1.25rem',
						}}
						color={passwordStrengthProgressColor}
						data-testid='password-change--strength-meter'
					/>
				)}
			</Stack>
		);
	};

	const clearFormState = () => {
		setOldPassword('');
		setPassword('');
		setShowOldPassword(false);
		setShowPassword(false);
	};

	const handleOnCancel = () => {
		clearFormState();
		onCancel();
	};

	const handleOnConfirm = async () => {
		const result = await onConfirm(oldPassword, password);
		// only clear form state if password is successfully changed
		result && clearFormState();
	};

	return (
		<>
			<Dialog fullWidth open={open} aria-labelledby='change-password-title'>
				<DialogTitle id='change-password-title'>
					{CHANGE_PASSWORD_ACTION_TEXT()}
					<Typography variant='subtitle2' color='textSecondary'>
						{CHANGE_PASSWORD_SUBHEADER()}
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ mt: 2 }}>{formContent()}</DialogContent>
				<DialogActions sx={{ m: 2 }}>
					<Button
						onClick={handleOnCancel}
						color='primary'
						data-testid='password-change--cancel'
					>
						{CHANGE_PASSWORD_CANCEL_ACTION_TEXT()}
					</Button>
					<Button
						onClick={handleOnConfirm}
						color='primary'
						variant='contained'
						disabled={!isFormValid}
						data-testid='password-change--confirm'
					>
						{CHANGE_PASSWORD_CONFIRM_ACTION_TEXT()}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ChangePasswordModal;
